import dayjs from 'dayjs'
import type { EventDescription, Pricings } from '~/composables/types/event'
import type { EventDateCartItem } from '~/composables/types/cart'
import { eventDateGetters } from '~/utils/eventDateGetters'
import { eventGetters } from '~/utils/eventGetters'

function resolveDurationMinutes(event: EventDescription): number {
  const nextDur = eventGetters.getNextDateAvailableDuration(event)
  if (nextDur != null && nextDur !== '') {
    const n =
      typeof nextDur === 'number' ? nextDur : parseInt(String(nextDur), 10)
    if (Number.isFinite(n) && n > 0) {
      return n
    }
  }
  const minDur = eventGetters.getEventMinimumDuration(event)
  if (minDur != null && minDur > 0) {
    return minDur
  }
  const def = eventGetters.getDefaultDuration(event)
  const d = typeof def === 'number' ? def : parseInt(String(def), 10)
  if (Number.isFinite(d) && d > 0) {
    return d
  }
  return 120
}

/**
 * Cart line for instant booking without a calendar `EventDate` id — same shape as
 * {@link parseEventDateToCartItem}, filled from event + user-selected start time.
 */
export function buildInstantBookingCartItem(
  event: EventDescription,
  quantity: number,
  selectedStartIso: string,
  pricings: Pricings,
  isPrivate: boolean
): EventDateCartItem {
  const start = dayjs(selectedStartIso)
  if (!start.isValid()) {
    throw new Error('Invalid selected date/time')
  }

  const end = start.add(resolveDurationMinutes(event), 'minute')
  const toCartDateTime = (d: dayjs.Dayjs) => d.format('YYYY-MM-DD HH:mm:ss')
  const dateStr = toCartDateTime(start)
  const endStr = toCartDateTime(end)

  const eventId = eventGetters.getId(event)
  const instantMax = eventGetters.getMaxInstantBookingTickets(event)
  const nextInstance = event.next_instance
  const dateType = isPrivate ? 'PRIVATE' : 'PUBLIC'

  return {
    type: 'EVENT',
    event: eventId,
    eventDescriptionId: eventId,
    quantity,
    title: eventGetters.getTitle(event),
    pricings,
    eventDescriptionPricing: event.pricing?.data,
    category: eventGetters.getMainCategory(event) || '',
    permalink: eventGetters.getPermalink(event),
    thumbnail: imageGetters.getThumb(eventGetters.getPrincipalImage(event)),
    requiredAddress: eventGetters.getIsAddressRequired(event) || false,
    availableTicketsQuantity: instantMax,
    privateMinTickets: eventDateGetters.getPrivateMinTickets(nextInstance),
    privateMaxTickets: eventDateGetters.getPrivateMaxTickets(nextInstance),
    dateType,
    date: dateStr,
    startTime: dateStr,
    endTime: endStr,
    all_day: false,
    private: isPrivate,
    dateTypeToBeBooked: isPrivate ? 'PRIVATE' : 'PUBLIC',
    attendanceMode: eventGetters.getAttendanceMode(event),
    hasShippingCosts: eventGetters.hasShippingCosts(event),
    supplier: eventGetters.getSupplierName(event),
    supplierId: eventGetters.getSupplierId(event),
    needsMajority: eventGetters.getNeedsMajority(event),
    specialization: eventGetters.getSpecialization(event),
    instantBooking: true,
  }
}
